import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { tagColor } from "./data";
import { hasAcknowledged } from "../../helper/progressNoteFormatters";

/**
 * One feed note card. Source: Figma note card inside the Progress notes feed
 * (2156:3257). Top→bottom: a colour-coded category TAG chip, then the note body,
 * then a footer with a round purple avatar + author name over a timestamp on the
 * left, and two reaction metric groups (reply / thumbs-up counts) on the right.
 *
 * Both reaction groups are live: the thumb acknowledges the note, and reply hands
 * the card up so the full-screen "New comment" composer can open on it (Figma
 * 2168:3257). Replying is NOT an inline textarea — the design gives a comment the
 * same editor and Tasks panel a note gets. The comment list hangs under the
 * footer.
 *
 * Takes a CARD, never a wire note: `card` is progressNoteFormatters.toCard's
 * output. The Figma title/subhead lines aren't separate fields on the wire —
 * real bodies carry their own heading inside the markup (e.g. "AppointmentId:
 * 17410 <br />…"), so the body block is a single rendered blob.
 *
 * This component OWNS no server state. It reports intent through the callbacks
 * and lets ProgressNotes refetch; the counts it renders are always the ones the
 * server last returned, never a local guess.
 */

/**
 * Bridge's numberOfCommentsToShow. A note is allowed to carry an unbounded
 * comment thread (a live one already holds 7), and a feed of fully-expanded
 * threads would bury the notes themselves — so the card shows the newest two and
 * hides the rest behind a toggle.
 */
const COMMENTS_TO_SHOW = 2;

/**
 * The board's reply glyph is a REPLY ARROW, not a speech bubble: a head pointing
 * left, a shaft running right, and a rounded corner dropping into a short stub
 * (↰). Measured off the feed export — a 12×12 glyph inside the 16px metric box.
 */
const ReplyIcon = () => (
  <svg className="pn-metric-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M6 3L2 6.75 6 10.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 6.75h10a2 2 0 012 2v4.5"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ThumbIcon = () => (
  <svg className="pn-metric-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M5 7l2.6-4.4c.5-.8 1.7-.5 1.7.5V6h3.1c.9 0 1.5.8 1.3 1.6l-.9 4c-.1.6-.7 1-1.3 1H5V7z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M5 7H3.2c-.5 0-.9.4-.9.9v3.8c0 .5.4.9.9.9H5V7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const ALREADY_LIKED = "You have already acknowledged it.";

const NoteCard = ({ card, meId, onReply, onLikeNote, onLikeComment }) => {
  const tag = tagColor(card.category);

  const [likingNote, setLikingNote] = useState(false);
  // Which comment's like is in flight — an id, not a bool, so two comments on the
  // same card can't share one spinner.
  const [likingCommentId, setLikingCommentId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Every callback below settles asynchronously and can outlive the card (a chip
  // or a search replaces the whole list mid-flight), so each resolution checks
  // this before touching state.
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const likedNote = hasAcknowledged(card.acknowledgers, meId);
  const comments = card.comments || [];
  const shown = expanded ? comments : comments.slice(0, COMMENTS_TO_SHOW);
  const hiddenCount = comments.length - shown.length;

  const likeNote = () => {
    // The server rejects a second acknowledgement outright, so a card that
    // already carries this employee reports the state and stops here rather than
    // spending a request to be told no.
    if (likedNote || likingNote) return;
    setLikingNote(true);
    Promise.resolve(onLikeNote(card.id)).then(
      () => {
        if (alive.current) setLikingNote(false);
      },
      () => {
        if (alive.current) setLikingNote(false);
      }
    );
  };

  const likeComment = (comment) => {
    if (hasAcknowledged(comment.acknowledgers, meId) || likingCommentId) return;
    setLikingCommentId(comment.id);
    Promise.resolve(onLikeComment(card.id, comment.id)).then(
      () => {
        if (alive.current) setLikingCommentId(null);
      },
      () => {
        if (alive.current) setLikingCommentId(null);
      }
    );
  };

  return (
    <article className="pn-note">
      <span className="pn-tag" style={{ background: tag.bg, color: tag.fg }}>
        {card.category}
      </span>

      <div className="pn-note-body">
        {/*
          Note bodies are server-stored HTML (<br />, and the composer's toolbar
          emits bold/italic/lists/links), so they cannot render as plain text.
          `card.html` is sanitizeBody's output and is the ONLY thing that may go
          in here — never a raw `note.body`, which is unescaped author input from
          the database and is exactly the injection hole bridge ships.
        */}
        {/* A div, not a <p>: the body can hold block elements (headings, lists,
            paragraphs) which are invalid inside a <p> and would break the layout. */}
        <div className="pn-note-text" dangerouslySetInnerHTML={{ __html: card.html }} />
      </div>

      <footer className="pn-note-foot">
        <div className="pn-author">
          <span className="pn-avatar" aria-hidden="true">
            {card.initials}
          </span>
          <span className="pn-author-meta">
            <span className="pn-author-name">{card.author}</span>
            <span className="pn-author-time">{card.time}</span>
          </span>
        </div>

        {/* [replies, acks] — comments.length and acknowledgers.length. */}
        <div className="pn-reactions">
          {/* Opens the composer on this card — the card holds no draft state. */}
          <button
            type="button"
            className="pn-metric"
            aria-label={`Reply (${card.reactions[0]})`}
            aria-haspopup="dialog"
            title="Reply"
            onClick={() => onReply(card)}
          >
            <ReplyIcon />
            <span>{card.reactions[0]}</span>
          </button>
          {/*
            Deliberately NOT `disabled` when already liked: a disabled button is
            dropped from the tab order and browsers suppress its tooltip, so the
            one state that most needs explaining would be the one a keyboard or
            hover user could not reach. aria-pressed carries the state, the click
            handler enforces it, and .is-liked shows it.
          */}
          <button
            type="button"
            className={`pn-metric${likedNote ? " is-liked" : ""}`}
            aria-label={`Like (${card.reactions[1]})`}
            aria-pressed={likedNote}
            disabled={likingNote}
            title={likedNote ? ALREADY_LIKED : "Like"}
            onClick={likeNote}
          >
            <ThumbIcon />
            <span>{card.reactions[1]}</span>
          </button>
        </div>
      </footer>

      {shown.length ? (
        <ul className="pn-comments">
          {/* Already newest-first out of toCard — do not re-sort. */}
          {shown.map((comment) => {
            const likedComment = hasAcknowledged(comment.acknowledgers, meId);
            return (
              <li className="pn-comment" key={comment.id}>
                <span className="pn-avatar pn-avatar-sm" aria-hidden="true">
                  {comment.initials}
                </span>
                <div className="pn-comment-main">
                  <div className="pn-comment-head">
                    <span className="pn-comment-author">{comment.author}</span>
                    <span className="pn-comment-time">{comment.time}</span>
                  </div>
                  {/*
                    Same rule as the note body above: `comment.html` is
                    toComment()'s sanitizeBody output and is the only thing that
                    may be injected — never `comment.body` off the wire.
                  */}
                  <div
                    className="pn-comment-text"
                    dangerouslySetInnerHTML={{ __html: comment.html }}
                  />
                  <button
                    type="button"
                    className={`pn-comment-like${likedComment ? " is-liked" : ""}`}
                    aria-label={`Like comment by ${comment.author} (${comment.acks})`}
                    aria-pressed={likedComment}
                    disabled={likingCommentId === comment.id}
                    title={likedComment ? ALREADY_LIKED : "Like this comment"}
                    onClick={() => likeComment(comment)}
                  >
                    <ThumbIcon />
                    <span>{comment.acks}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {comments.length > COMMENTS_TO_SHOW ? (
        <button
          type="button"
          className="pn-comments-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded
            ? "Show fewer comments"
            : `Show ${hiddenCount} more ${hiddenCount === 1 ? "comment" : "comments"}`}
        </button>
      ) : null}
    </article>
  );
};

NoteCard.propTypes = {
  /** progressNoteFormatters.toCard output — `html` is already sanitised. */
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    html: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    reactions: PropTypes.arrayOf(PropTypes.number).isRequired,
    /** toComment() output, newest-first. */
    comments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        html: PropTypes.string.isRequired,
        author: PropTypes.string.isRequired,
        initials: PropTypes.string.isRequired,
        time: PropTypes.string.isRequired,
        acks: PropTypes.number.isRequired,
        acknowledgers: PropTypes.array.isRequired,
      })
    ).isRequired,
    /** Raw wire acknowledgers — `{name, employeeId}` — for hasAcknowledged. */
    acknowledgers: PropTypes.array.isRequired,
  }).isRequired,
  /** The signed-in EMPLOYEE's id, to spot our own acknowledgement. */
  meId: PropTypes.number,
  /** (card) -> void. Asks the feed to open the "New comment" composer on it. */
  onReply: PropTypes.func.isRequired,
  /** (noteId) -> Promise. */
  onLikeNote: PropTypes.func.isRequired,
  /** (noteId, commentId) -> Promise. */
  onLikeComment: PropTypes.func.isRequired,
};

export default NoteCard;
